import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Activity,
  Eye,
  KeyRound,
  CreditCard,
  Zap,
  RotateCw,
  Wallet,
  UserPlus,
  Trash2,
  ShieldAlert,
  Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const ACTION_META = {
  "credential.saved": { icon: KeyRound, cls: "bg-primary/15 text-primary" },
  "credential.rotated": { icon: RotateCw, cls: "bg-amber-500/15 text-amber-500" },
  "credential.viewed": { icon: Eye, cls: "bg-muted text-muted-foreground" },
  "card.tokenized": { icon: CreditCard, cls: "bg-primary/15 text-primary" },
  "card.used": { icon: Zap, cls: "bg-accent/15 text-accent" },
  "card.rotated": { icon: RotateCw, cls: "bg-amber-500/15 text-amber-500" },
  "wallet.saved": { icon: Wallet, cls: "bg-primary/15 text-primary" },
  "wallet.rotated": { icon: RotateCw, cls: "bg-amber-500/15 text-amber-500" },
  "wallet.viewed": { icon: Eye, cls: "bg-muted text-muted-foreground" },
  "merchant.created": { icon: UserPlus, cls: "bg-accent/15 text-accent" },
  "merchant.deleted": { icon: Trash2, cls: "bg-destructive/15 text-destructive" },
  "merchant.vault_viewed": { icon: ShieldAlert, cls: "bg-accent/15 text-accent" },
};

const PAGE_SIZE = 15;

export default function AuditTrail() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [merchants, setMerchants] = useState([]);
  const [merchantId, setMerchantId] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (isAdmin) {
      api
        .get("/admin/merchants", { params: { page: 1, page_size: 100 } })
        .then((res) => setMerchants(res.data.merchants))
        .catch(() => {});
    }
  }, [isAdmin]);

  const buildParams = (withPage = true) => {
    const p = {};
    if (withPage) { p.page = page; p.page_size = PAGE_SIZE; }
    if (isAdmin && merchantId !== "all") p.merchant_id = merchantId;
    if (isAdmin && startDate) p.start = startDate;
    if (isAdmin && endDate) p.end = endDate;
    return p;
  };

  useEffect(() => {
    api
      .get("/audit", { params: buildParams() })
      .then((res) => {
        setLogs(res.data.logs);
        setTotal(res.data.total);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, merchantId, startDate, endDate, isAdmin]);

  const onFilterChange = (v) => {
    setMerchantId(v);
    setPage(1);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/audit/export", { params: buildParams(false), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "citidan_audit_trail.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Audit trail exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-primary">Compliance</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Audit Trail</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isAdmin
              ? "Every view, change, and rotation of sensitive records across all merchants."
              : "A timeline of every time your sensitive records were viewed or changed."}
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                data-testid="audit-start-date"
                className="w-40"
                aria-label="Start date"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                data-testid="audit-end-date"
                className="w-40"
                aria-label="End date"
              />
              {(startDate || endDate) && (
                <Button variant="ghost" size="sm" onClick={() => { setStartDate(""); setEndDate(""); setPage(1); }} data-testid="audit-clear-dates">
                  Clear
                </Button>
              )}
            </div>
            <Select value={merchantId} onValueChange={onFilterChange}>
              <SelectTrigger className="w-56" data-testid="audit-merchant-filter">
                <SelectValue placeholder="All merchants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All merchants</SelectItem>
                {merchants.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.company || m.name} · {m.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="audit-export-btn">
              <Download className="mr-1.5 h-4 w-4" /> {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-6" data-testid="audit-list">
        <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Activity className="h-4 w-4 text-primary" /> {total} recorded events
        </div>
        {logs.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No audit events yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {logs.map((l) => {
              const meta = ACTION_META[l.action] || { icon: Activity, cls: "bg-muted text-muted-foreground" };
              const Icon = meta.icon;
              return (
                <div key={l.id} className="flex items-start gap-3 py-3" data-testid={`audit-row-${l.id}`}>
                  <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.cls}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{l.action}</span>
                      {isAdmin && (
                        <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-mono">{l.actor_email}</span>
                      )}
                    </div>
                    <div className="mt-0.5 text-sm">{l.summary}</div>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        )}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between" data-testid="audit-pagination">
            <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-prev">
              Previous
            </Button>
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
            <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="audit-next">
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
